import { useState, isValidElement, Children, type ReactNode, type ReactElement, type ComponentType, type ComponentProps, Fragment } from 'react'
import {
  ChevronDown,
  Info,
  Lightbulb,
  AlertTriangle,
  AlertOctagon,
  CheckCircle2,
  BookOpen,
  Quote,
  Pencil,
  Sparkles,
} from 'lucide-react'
import type { ExtraProps } from 'react-markdown'
import { ExamWeightLabel } from '@/components/ExamWeightLabel'
import { parseExamWeight, splitWeightTag } from '@/lib/examWeight'

// Matches an Obsidian callout header at the start of a blockquote paragraph:
//   [!type]        non-foldable
//   [!type]-       foldable, collapsed by default
//   [!type]+       foldable, expanded by default
// followed by an optional title on the same line.
const HEADER_RE = /^\s*\[!(\w+)\]([-+]?)[ \t]*([^\n]*)/

type CalloutStyle = {
  icon: ComponentType<{ className?: string }> | null
  borderClass: string
  accentClass: string
  roundLeft?: boolean
  noBorder?: boolean
  bgClass?: string
  contentClass?: string
  /** Replaces the rounding `roundLeft` picks. */
  roundClass?: string
  /** Replaces the default `my-4` between callouts. */
  marginClass?: string
  /** Padding for the header row (and the toggle button that wraps it). */
  headerClass?: string
  /** Replaces the grey hover wash on a foldable header. */
  hoverClass?: string
  /**
   * The body runs straight on from the header: no hairline between them, and
   * no italic lead paragraph (that treatment is for a learning objective's
   * one-line summary, not an answer).
   */
  plainBody?: boolean
  /**
   * A tinted note card in the shape of a question's examiner's report
   * (`ExaminerReportCard`): no side rule, the icon and a small bold title in
   * the accent, the body at reading size. Never folded — a card is read in
   * place, not opened.
   */
  card?: boolean
}

const DEFAULT_STYLE: CalloutStyle = {
  icon: Pencil,
  borderClass: 'border-border',
  accentClass: 'text-muted-foreground',
}

const SKY: CalloutStyle = {
  icon: Info,
  borderClass: 'border-sky-500/60',
  accentClass: 'text-sky-600 dark:text-sky-400',
}

const TEAL: CalloutStyle = {
  icon: Lightbulb,
  borderClass: 'border-teal-500/60',
  accentClass: 'text-teal-600 dark:text-teal-400',
}

const EXAMPLE_STYLE: CalloutStyle = {
  icon: null,
  borderClass: 'border-slate-300 dark:border-slate-500',
  accentClass: 'text-foreground',
  roundLeft: true,
  noBorder: true,
  bgClass: 'bg-card',
  contentClass: 'text-base text-muted-foreground',
}

const EMERALD: CalloutStyle = {
  icon: CheckCircle2,
  borderClass: 'border-emerald-500/60',
  accentClass: 'text-emerald-600 dark:text-emerald-400',
  roundLeft: true,
  noBorder: true,
}

const AMBER: CalloutStyle = {
  icon: AlertTriangle,
  borderClass: 'border-amber-500/60',
  accentClass: 'text-amber-600 dark:text-amber-400',
}

const RED: CalloutStyle = {
  icon: AlertOctagon,
  borderClass: 'border-red-500/60',
  accentClass: 'text-red-600 dark:text-red-400',
}

// A question on a guide page ("Do I need a degree?"). These stack into an FAQ,
// so each is a soft grey card rather than an aside hung off a coloured rule:
// no side border, no icon repeated down the page, and the answer set at the
// same size as the prose around it.
const QUESTION_STYLE: CalloutStyle = {
  icon: null,
  borderClass: '',
  accentClass: 'text-foreground',
  noBorder: true,
  roundClass: 'rounded-xl',
  marginClass: 'my-3',
  bgClass: 'bg-muted/60 dark:bg-white/[0.06]',
  hoverClass: 'hover:bg-muted dark:hover:bg-white/[0.09]',
  headerClass: 'px-5 py-4',
  contentClass: 'text-base text-foreground',
  plainBody: true,
}

const VIOLET: CalloutStyle = {
  icon: AlertOctagon,
  borderClass: 'border-violet-500/60',
  accentClass: 'text-violet-500 dark:text-violet-400',
}

const CYAN: CalloutStyle = {
  icon: BookOpen,
  borderClass: 'border-cyan-500/60',
  accentClass: 'text-cyan-600 dark:text-cyan-400',
}

const QUOTE_STYLE: CalloutStyle = {
  icon: Quote,
  borderClass: 'border-border',
  accentClass: 'text-muted-foreground',
}

// A concept page's "The idea" — why the formula has the shape it has, in
// words. Violet, which the meaning map (style guide §4.1) keeps for it, so the
// explanation never reads as a verdict or as the blue examiner's report.
const IDEA_STYLE: CalloutStyle = {
  icon: Sparkles,
  borderClass: '',
  accentClass: 'text-violet-900 dark:text-violet-100',
  bgClass: 'bg-violet-50 dark:bg-violet-950/50',
  card: true,
}

// A trap set beside the idea it distorts, rather than folded into the page's
// closing "Exam traps" list. Amber: the caution hue.
const TRAP_STYLE: CalloutStyle = {
  icon: AlertTriangle,
  borderClass: '',
  accentClass: 'text-amber-900 dark:text-amber-100',
  bgClass: 'bg-amber-50 dark:bg-amber-950/50',
  card: true,
}

const STYLE_MAP: Record<string, CalloutStyle> = {
  note: DEFAULT_STYLE,
  info: SKY,
  todo: SKY,
  tip: TEAL,
  hint: TEAL,
  important: VIOLET,
  example: EXAMPLE_STYLE,
  answer: EMERALD,
  success: EMERALD,
  check: EMERALD,
  done: EMERALD,
  warning: AMBER,
  caution: AMBER,
  attention: AMBER,
  danger: RED,
  error: RED,
  failure: RED,
  fail: RED,
  missing: RED,
  bug: RED,
  question: QUESTION_STYLE,
  faq: QUESTION_STYLE,
  help: QUESTION_STYLE,
  quote: QUOTE_STYLE,
  cite: QUOTE_STYLE,
  abstract: CYAN,
  summary: CYAN,
  tldr: CYAN,
  idea: IDEA_STYLE,
  trap: TRAP_STYLE,
}

function getStyle(type: string): CalloutStyle {
  return STYLE_MAP[type] ?? DEFAULT_STYLE
}

function titleCase(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s
}

// Splits "General Probability {23–30%}" into text + pill badges for each {…}.
function renderTitle(raw: string): ReactNode {
  const parts = raw.split(/(\{[^}]+\})/)
  if (parts.length === 1) return raw
  return (
    <Fragment>
      {parts.map((part, i) =>
        part.startsWith('{') && part.endsWith('}') ? (
          <span
            key={i}
            className="ml-1.5 inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium bg-primary/10 text-primary align-middle"
          >
            {part.slice(1, -1)}
          </span>
        ) : (
          part || null
        ),
      )}
    </Fragment>
  )
}

// Is this child the blockquote's first paragraph?
//
// react-markdown hands us the intrinsic `<p>` element normally, but when the
// consumer overrides `components.p` (WikiArticle does, to unwrap distribution
// simulators) the element's type is that component instead. react-markdown
// renders custom components with `passNode`, so the original tag is still
// available on the `node` prop — check both spellings.
function isParagraph(child: ReactElement): boolean {
  if (child.type === 'p') return true
  const node = (child.props as { node?: { tagName?: string } }).node
  return node?.tagName === 'p'
}

interface MatchResult {
  type: string
  fold: '' | '-' | '+'
  title: string
  body: ReactNode[]
}

function matchCallout(children: ReactNode): MatchResult | null {
  const childArray = Children.toArray(children)
  if (childArray.length === 0) return null

  // Exam-page callouts frequently have shape:
  //   > [!answer]- Title
  //   >
  //   > <div>…</div> or a table or list
  // which react-markdown renders with a leading whitespace text node and/or a
  // non-<p> first child (table, div, ul, …). We scan forward to find the
  // first node that contains the `[!type]` header — usually the opening text
  // of the first <p>, but can be a bare string child too.

  let headerIdx = -1
  let match: RegExpMatchArray | null = null
  let headerString: string | null = null
  let headerInsideP = false

  for (let i = 0; i < childArray.length; i++) {
    const child = childArray[i]

    if (typeof child === 'string') {
      if (child.trim() === '') continue
      const m = child.match(HEADER_RE)
      if (m) {
        headerIdx = i
        match = m
        headerString = child
        headerInsideP = false
        break
      }
      return null
    }

    if (isValidElement(child) && isParagraph(child as ReactElement)) {
      const pKids = Children.toArray(
        ((child as ReactElement).props as { children?: ReactNode }).children ?? [],
      )
      if (pKids.length === 0) continue
      const first = pKids[0]
      if (typeof first !== 'string') return null
      if (first.trim() === '' && pKids.length === 1) continue
      const m = first.match(HEADER_RE)
      if (!m) return null
      headerIdx = i
      match = m
      headerString = first
      headerInsideP = true
      break
    }

    // First substantive child isn't a string or <p> — not a callout we can parse.
    return null
  }

  if (!match || headerString === null) return null

  const type = match[1].toLowerCase()
  const fold = (match[2] === '-' || match[2] === '+' ? match[2] : '') as '' | '-' | '+'
  const titleTail = (match[3] ?? '').trim()

  const body: ReactNode[] = []

  if (headerInsideP) {
    const headerChild = childArray[headerIdx] as ReactElement
    const pKids = Children.toArray(
      ((headerChild as ReactElement).props as { children?: ReactNode }).children ?? [],
    )
    const newlineIdx = headerString.indexOf('\n')
    const remainingFirstText = newlineIdx >= 0 ? headerString.slice(newlineIdx + 1) : ''
    const remainingPKids = pKids.slice(1)

    if (remainingFirstText.length > 0 || remainingPKids.length > 0) {
      const newPKids: ReactNode[] = []
      if (remainingFirstText.length > 0) newPKids.push(remainingFirstText)
      for (const c of remainingPKids) newPKids.push(c)
      body.push(<p key="callout-p-head">{newPKids}</p>)
    }
  } else {
    const newlineIdx = headerString.indexOf('\n')
    const remaining = newlineIdx >= 0 ? headerString.slice(newlineIdx + 1) : ''
    if (remaining.length > 0) body.push(remaining)
  }

  for (let i = headerIdx + 1; i < childArray.length; i++) {
    body.push(childArray[i])
  }

  return { type, fold, title: titleTail, body }
}

interface CalloutProps {
  type: string
  fold: '' | '-' | '+'
  title: string
  children: ReactNode
}

// The hairline under the header and the body's padding below it.
const BODY_FRAME = 'border-t border-border/40 px-4 pb-4 pt-3'
// A learning objective's first line is its summary, set apart in muted italics.
const LEAD_PARAGRAPH = '[&>p:first-of-type]:text-muted-foreground [&>p:first-of-type]:italic [&>p:first-of-type]:mb-3'

const CONTENT_CLASSES_SHARED = [
  'leading-relaxed',
  '[&>p]:my-1.5',
  '[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2',
  '[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-2',
  '[&_li]:my-1',
  '[&>ul]:my-1.5 [&>ul]:pl-4 [&_ul]:list-disc',
  '[&>ol]:my-1.5 [&>ol]:pl-4 [&_ol]:list-decimal',
  '[&_li::marker]:text-muted-foreground/60',
  '[&_strong]:font-semibold',
  '[&_a]:text-primary [&_a]:underline',
  '[&_table]:w-full [&_table]:border-collapse [&_table]:mt-2 [&_table]:text-sm',
  '[&_th]:py-2 [&_th]:px-3 [&_th]:text-left [&_th]:text-xs [&_th]:font-medium [&_th]:uppercase [&_th]:tracking-wide [&_th]:text-muted-foreground [&_th]:border-b [&_th]:border-border/60',
  '[&_td]:py-2 [&_td]:px-3 [&_td]:border-b [&_td]:border-border/30 [&_td]:align-top',
  '[&_tbody_tr:last-child_td]:border-0',
].join(' ')

const CONTENT_CLASSES = `text-sm text-foreground ${BODY_FRAME} ${LEAD_PARAGRAPH} ${CONTENT_CLASSES_SHARED}`

// A note card's body: the article's reading size, flush with the card's padding.
const CARD_BODY_CLASSES = `text-base text-foreground [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 ${CONTENT_CLASSES_SHARED}`

function NoteCard({ style, title, children }: { style: CalloutStyle; title: string; children: ReactNode }) {
  const Icon = style.icon
  return (
    <section aria-label={title} className={`not-prose my-4 space-y-2 rounded-xl p-4 ${style.bgClass ?? 'bg-muted'}`}>
      <p className={`flex items-center gap-1.5 text-sm font-semibold ${style.accentClass}`}>
        {Icon && <Icon aria-hidden="true" className="h-4 w-4 shrink-0" />}
        {renderTitle(title)}
      </p>
      {Children.count(children) > 0 && <div className={CARD_BODY_CLASSES}>{children}</div>}
    </section>
  )
}

function Callout({ type, fold, title, children }: CalloutProps) {
  const style = getStyle(type)
  if (style.card) {
    return (
      <NoteCard style={style} title={title || titleCase(type)}>
        {children}
      </NoteCard>
    )
  }
  return <FoldingCallout type={type} fold={fold} title={title} style={style}>{children}</FoldingCallout>
}

function FoldingCallout({ type, fold, title, style, children }: CalloutProps & { style: CalloutStyle }) {
  const collapsible = fold !== ''
  const [open, setOpen] = useState(fold !== '-')
  const Icon = style.icon
  const hasBody = Children.count(children) > 0

  // A learning objective's `{23-30%}` share of the exam is lifted out of the
  // title and rendered as its own right-aligned label, the same one the quiz
  // builder uses (`ExamWeightLabel`) — so the figures line up in a column
  // instead of trailing titles of ragged length. Any other `{…}` tag stays in
  // the title and keeps the inline pill.
  const { title: baseTitle, weight: examWeight } =
    type === 'example' ? splitWeightTag(title) : { title, weight: null }
  const displayTitle = baseTitle || titleCase(type)
  const examPercentage = parseExamWeight(examWeight)
  const isBarGraph = examPercentage !== null

  const headerContent = (
    <div className="flex items-center gap-3 w-full">
      {Icon && <Icon className={`h-4 w-4 shrink-0 ${style.accentClass}`} />}
      <span className={`font-medium flex-1 text-left ${style.noBorder ? 'text-base' : 'text-sm'} ${Icon ? 'text-foreground' : style.accentClass}`}>{renderTitle(displayTitle)}</span>
      {examWeight && <ExamWeightLabel weight={examWeight} />}
      {collapsible && hasBody && (
        <ChevronDown
          className={`${style.plainBody ? 'h-4 w-4' : 'h-3.5 w-3.5'} shrink-0 text-muted-foreground transition-transform duration-200 ${open ? '' : '-rotate-90'}`}
        />
      )}
    </div>
  )

  const roundClass = style.roundClass ?? (style.roundLeft ? 'rounded-lg' : 'rounded-r-lg')
  const borderClasses = style.noBorder ? '' : `border-l-[3px] ${style.borderClass}`
  const bgClass = style.bgClass ?? 'bg-card'
  const marginClass = style.marginClass ?? 'my-4'
  const headerClass = style.headerClass ?? 'px-4 py-3'
  const hoverClass = style.hoverClass ?? 'hover:bg-accent/50'
  const contentClasses = style.plainBody
    ? `${style.contentClass ?? 'text-sm text-foreground'} px-5 pb-5 pt-0 ${CONTENT_CLASSES_SHARED}`
    : style.contentClass
      ? `${style.contentClass} ${BODY_FRAME} ${LEAD_PARAGRAPH} ${CONTENT_CLASSES_SHARED}`
      : CONTENT_CLASSES

  if (isBarGraph) {
    return (
      <div className="not-prose my-4 rounded-lg overflow-hidden bg-background">
        <div className="relative">
          {/* The bar fills to the exam coverage % when collapsed, full width when
              expanded. It is filled in the colour of the exam whose page it is on
              (`--exam-accent-muted`, scoped by `WikiExam` — the soft wash read as a
              tint rather than the exam's colour), and plain card grey wherever no
              exam is in scope. */}
          <div
            className="absolute inset-y-0 left-0 bg-[color:var(--exam-accent-muted,hsl(var(--card)))] transition-all duration-300"
            style={{ width: open ? '100%' : `${examPercentage}%` }}
          />
          {collapsible && hasBody ? (
            <button
              type="button"
              data-callout-toggle
              onClick={() => setOpen(v => !v)}
              className="relative z-10 w-full px-4 py-3 text-left hover:bg-accent/30 transition-colors duration-150"
              aria-expanded={open}
            >
              {headerContent}
            </button>
          ) : (
            <div className="relative z-10 px-4 py-3">{headerContent}</div>
          )}
        </div>
        {hasBody && (
          <div data-callout-body hidden={!open} className={contentClasses}>
            {children}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className={`not-prose ${marginClass} ${borderClasses} ${bgClass} ${roundClass} overflow-hidden`}>
      {collapsible && hasBody ? (
        <button
          type="button"
          data-callout-toggle
          onClick={() => setOpen(v => !v)}
          className={`w-full ${headerClass} text-left ${hoverClass} transition-colors duration-150`}
          aria-expanded={open}
        >
          {headerContent}
        </button>
      ) : (
        <div className={headerClass}>{headerContent}</div>
      )}
      {hasBody && (
        <div data-callout-body hidden={!open} className={contentClasses}>
          {children}
        </div>
      )}
    </div>
  )
}

function classList(value: unknown): string[] {
  if (typeof value === 'string') return value.split(/\s+/)
  if (Array.isArray(value)) return value.flatMap(entry => (typeof entry === 'string' ? entry.split(/\s+/) : []))
  return []
}

/**
 * Does this blockquote hold a rendered formula?
 *
 * Matches the KaTeX root rather than `.katex-display`: the vault writes boxed
 * formulas as `> $$…$$` on one line, which remark parses as inline math, so the
 * display wrapper is never emitted. Both spellings end up as `.katex`.
 */
function hasRenderedMath(node: ReactNode): boolean {
  if (node === null || node === undefined) return false
  if (typeof node === 'string' || typeof node === 'number' || typeof node === 'boolean') return false
  if (Array.isArray(node)) return node.some(hasRenderedMath)
  if (isValidElement(node)) {
    const el = node as ReactElement
    const props = el.props as Record<string, unknown>
    if (classList(props.className).includes('katex')) return true
    if (props.children != null) return hasRenderedMath(props.children as ReactNode)
  }
  return false
}

/**
 * The markdown `blockquote`: an Obsidian callout when its first line is a
 * `[!type]` header, otherwise a plain box — a math focus target when it holds a
 * formula. Handed to react-markdown by `components/calloutComponents.ts`.
 */
export function CalloutBlockquote(props: ComponentProps<'blockquote'> & ExtraProps) {
  const { children, ...rest } = props
  const match = matchCallout(children)
  const isMathBlock = !match && hasRenderedMath(children)

  if (!match) {
    return (
      <blockquote
        {...rest}
        // `data-math-block` makes the whole box — padding included — the tap
        // target for math focus mode; the press itself is silent because the
        // overlay it opens plays the panel cue. See components/MathFocus.tsx.
        data-math-block={isMathBlock ? '' : undefined}
        data-sound={isMathBlock ? 'none' : undefined}
        className={`not-prose my-4 bg-background rounded-lg px-6 py-4 text-center [&_p]:m-0${
          isMathBlock ? ' cursor-zoom-in hover:ring-2 hover:ring-primary/40 transition-shadow' : ''
        }`}
      >
        {children}
      </blockquote>
    )
  }
  return (
    <Callout type={match.type} fold={match.fold} title={match.title}>
      {match.body}
    </Callout>
  )
}
